import type {
  HubTierClass,
  ScoreDetails,
  ScoreDetailsV4,
  ScoreFileDetail,
  ScorePenaltyV4,
} from '../../types';
import { justificationNotes } from './scoreCoaching';

export const TIER_DISPLAY: Record<HubTierClass, string> = {
  org: 'Org tier',
  domain: 'Domain tier',
  experimental: 'Experimental tier',
  unidentified: 'Unidentified',
  rejected: 'Rejected',
};

const RELEVANCE_DISPLAY: Record<string, string> = {
  core: 'Core',
  supporting: 'Supporting',
  irrelevant: 'Irrelevant',
};

export interface PenaltyCoachItem {
  id: string;
  cause: string;
  label: string;
  attachmentKind?: string;
  attachmentNames: string[];
  body: string;
  tone: 'say' | 'warn' | 'plain';
  pill: 'unidentified' | 'rejected' | 'default';
}

export interface ImprovementArea {
  id: string;
  title: string;
  badge?: string;
  lead: string;
  nextTime?: string[];
  items?: PenaltyCoachItem[];
}

export const NOTHING_TO_CHANGE =
  'Nothing to change on this prompt. Keep doing what you did here: a clear ask, the right files named, and skills that fit the task.';

const PENALTY_LEAD =
  'These came up on this prompt, and they cost you points every time they happen.';

const UNIDENTIFIED_ADVICE =
  'Get the skill file uploaded and approved in the HUB to get suitable recognition';

const REJECTED_SKILL_ADVICE =
  'Your attached skill is rejected by HUB and is not advised to use further. Please stop using it to avoid penalties in future';

const UNPLANNED_REWORK_ADVICE =
  'This session went back over work it had already done. Before you start, write down what "done" looks like and what is out of scope. When the first answer is close, ask for the one change you want instead of starting the task again.';

const WRONG_INTERPRETATION_ADVICE =
  'The AI read your request differently from how you meant it. Give the outcome and the main constraint in one line each, and name the file or function. If the first reply looks off, correct it straight away, before more work builds on it.';

const CLARITY_NEXT = [
  'Open with the outcome you want in one sentence.',
  'List the rules it must follow, so nothing is left to guesswork.',
  'Say what is out of scope, and keep to one request per prompt.',
];

const CONTEXT_NEXT = [
  'Name the file, function or component you want changed.',
  'Include the error message, failing test or snippet when there is one.',
  'Mention the related files or tests, and the pattern to follow.',
];

const TYPE_NEXT = [
  'Check HUB first, and pick an org-approved skill when one covers the task.',
  'If you rely on a skill often, ask its owner to put it forward for wider approval.',
];

const RELEVANCE_NEXT = [
  "Read a skill's one-line purpose, then ask: does this help with what I just wrote?",
  'Attach fewer skills that fit, and drop the rest.',
];

const CAUSE_ORDER = ['unidentified', 'rejected', 'unplanned_rework', 'wrong_interpretation'];

const INTENT_CAUSES = new Set(['unplanned_rework', 'wrong_interpretation']);

export function isIntentPenaltyCause(cause: string | null | undefined): boolean {
  return INTENT_CAUSES.has((cause || '').toLowerCase());
}

export function isScoreDetailsV4(details: ScoreDetails | null | undefined): details is ScoreDetailsV4 {
  return !!details && typeof details === 'object' && 'v' in details && details.v === 4;
}

export function formatRelevanceLabel(label: string | null | undefined): string | null {
  if (!label) return null;
  const key = label.trim().toLowerCase();
  return RELEVANCE_DISPLAY[key] ?? label;
}

export function penaltyCauseText(penalty: ScorePenaltyV4): string {
  const name = penalty.name?.trim();
  const cause = (penalty.cause || '').toLowerCase();
  if (cause === 'rejected') {
    return name ? `The attachment ${name} was rejected.` : 'An attachment was rejected.';
  }
  if (cause === 'unidentified') {
    return name
      ? `${name} is not in the Hub.`
      : 'A skill could not be identified.';
  }
  if (cause === 'unplanned_rework') {
    return 'This session needed unplanned rework.';
  }
  if (cause === 'wrong_interpretation') {
    return 'The assistant misread the ask.';
  }
  return name ? `${name}.` : cause.replace(/_/g, ' ');
}

export function penaltyTitle(penalty: ScorePenaltyV4): string {
  const cause = (penalty.cause || '').toLowerCase();
  if (cause === 'rejected') return 'Rejected attachment';
  if (cause === 'unidentified') return 'Unidentified skill';
  if (cause === 'unplanned_rework') return 'Session intent: unplanned rework';
  if (cause === 'wrong_interpretation') return 'Session intent: wrong interpretation';
  return 'Penalty';
}

function finiteBelow(value: number | null | undefined, target: number): boolean {
  if (value === null || value === undefined) return false;
  const n = Number(value);
  return Number.isFinite(n) && n < target;
}

function scoreMissing(value: number | null | undefined): boolean {
  if (value === null || value === undefined) return true;
  return !Number.isFinite(Number(value));
}

function causeRank(cause: string): number {
  const index = CAUSE_ORDER.indexOf(cause);
  return index === -1 ? CAUSE_ORDER.length : index;
}

function rejectedAdvice(kind: string | null | undefined): string {
  const normalized = (kind || '').toLowerCase();
  if (normalized === 'rule') {
    return 'Your attached rule is rejected by HUB and is not advised to use further. Please stop using it to avoid penalties in future';
  }
  if (normalized === 'agent') {
    return 'Your attached agent is rejected by HUB and is not advised to use further. Please stop using it to avoid penalties in future';
  }
  if (normalized === 'skill' || !normalized) return REJECTED_SKILL_ADVICE;
  return 'This attachment is rejected by HUB and is not advised to use further. Please stop using it to avoid penalties in future';
}

function penaltyAdvice(cause: string, kind: string | null | undefined, name: string | null): string {
  if (cause === 'unidentified') return UNIDENTIFIED_ADVICE;
  if (cause === 'rejected') return rejectedAdvice(kind);
  if (cause === 'unplanned_rework') return UNPLANNED_REWORK_ADVICE;
  if (cause === 'wrong_interpretation') return WRONG_INTERPRETATION_ADVICE;
  const trimmed = name?.trim();
  return trimmed
    ? `${trimmed} added a penalty on this prompt. Leave it off the next one.`
    : 'This penalty came up on this prompt. Leave out whatever caused it next time.';
}

function penaltyLabel(cause: string, kind: string | null | undefined, count = 1): string {
  const plural = count > 1;
  const normalized = (kind || '').toLowerCase();
  if (cause === 'unidentified') return plural ? 'Unidentified skills' : 'Unidentified skill';
  if (cause === 'rejected') {
    if (normalized === 'rule') return plural ? 'Rejected rules' : 'Rejected rule';
    if (normalized === 'agent') return plural ? 'Rejected agents' : 'Rejected agent';
    if (normalized === 'skill' || !normalized) return plural ? 'Rejected skills' : 'Rejected skill';
    return plural ? 'Rejected attachments' : 'Rejected attachment';
  }
  if (cause === 'unplanned_rework') return 'Unplanned rework';
  if (cause === 'wrong_interpretation') return 'Wrong interpretation';
  return plural ? 'Penalties' : 'Penalty';
}

function attachmentKindLabel(cause: string, kind: string | null | undefined): string | undefined {
  const normalized = (kind || '').toLowerCase();
  if (cause !== 'unidentified' && cause !== 'rejected') return undefined;
  if (normalized === 'rule') return 'Rule';
  if (normalized === 'agent') return 'Agent';
  if (normalized === 'skill' || cause === 'unidentified' || !normalized) return 'Skill';
  return 'Attachment';
}

function penaltyPill(cause: string): PenaltyCoachItem['pill'] {
  if (cause === 'unidentified') return 'unidentified';
  if (cause === 'rejected') return 'rejected';
  return 'default';
}

function penaltyTone(cause: string): PenaltyCoachItem['tone'] {
  if (cause === 'unidentified') return 'say';
  if (cause === 'rejected') return 'warn';
  return 'plain';
}

function collectPenaltyItems(v4: ScoreDetailsV4): PenaltyCoachItem[] {
  const raw = (v4.derived?.penalties ?? []).map((penalty) => ({
    cause: (penalty.cause || '').toLowerCase(),
    name: penalty.name,
    kind: penalty.kind,
  }));
  const intent = (v4.session_intent || '').trim().toLowerCase();
  if (
    (intent === 'unplanned_rework' || intent === 'wrong_interpretation') &&
    !raw.some((penalty) => penalty.cause === intent)
  ) {
    raw.push({ cause: intent, name: null, kind: null });
  }

  return raw
    .map((penalty, index) => ({ penalty, index, rank: causeRank(penalty.cause) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ penalty, index }) => {
      const name = penalty.name?.trim() || '';
      const kindLabel = name ? attachmentKindLabel(penalty.cause, penalty.kind) : undefined;
      return {
        id: `${penalty.cause || 'penalty'}-${index}`,
        cause: penalty.cause,
        label: penaltyLabel(penalty.cause, penalty.kind),
        attachmentKind: kindLabel,
        attachmentNames: kindLabel && name ? [name] : [],
        body: penaltyAdvice(penalty.cause, penalty.kind, penalty.name),
        tone: penaltyTone(penalty.cause),
        pill: penaltyPill(penalty.cause),
      };
    });
}

/** One row per cause, listing every file that shares it. */
function groupFilePenaltyItems(items: PenaltyCoachItem[]): PenaltyCoachItem[] {
  const groups = new Map<string, PenaltyCoachItem>();
  items.forEach((item) => {
    const key = `${item.cause}|${item.attachmentKind || ''}|${item.body}`;
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, { ...item, attachmentNames: [...item.attachmentNames] });
      return;
    }
    item.attachmentNames.forEach((name) => {
      if (!existing.attachmentNames.includes(name)) existing.attachmentNames.push(name);
    });
    existing.label = penaltyLabel(existing.cause, existing.attachmentKind?.toLowerCase(), existing.attachmentNames.length);
    existing.id = `${existing.cause || 'penalty'}-files`;
  });
  return [...groups.values()];
}

function intentArea(items: PenaltyCoachItem[]): ImprovementArea {
  if (items.length === 1) {
    return {
      id: 'penalty-intent',
      title: items[0].label,
      badge: 'Session intent',
      lead: items[0].body,
    };
  }
  return {
    id: 'penalty-intent',
    title: 'Session intent',
    badge: 'Session intent',
    lead: 'This session was scored for how the request was understood, and for work that had to be done again.',
    items,
  };
}

function typeScoreLead(v4: ScoreDetailsV4): string {
  if (!scoreMissing(v4.derived?.type_score)) {
    return 'Some of the skills you attached have a lower approval level in HUB. Skills approved for the whole org carry the most weight.';
  }
  if (skillRows(v4).length === 0) {
    return 'This prompt did not use a skill approved in HUB. Skills approved for the whole org carry the most weight.';
  }
  return 'None of the skills you attached are approved in HUB. Skills approved for the whole org carry the most weight.';
}

export function collectImprovementAreas(v4: ScoreDetailsV4): ImprovementArea[] {
  const areas: ImprovementArea[] = [];
  const penaltyItems = collectPenaltyItems(v4);
  const fileItems = groupFilePenaltyItems(penaltyItems.filter((item) => !isIntentPenaltyCause(item.cause)));
  const intentItems = penaltyItems.filter((item) => isIntentPenaltyCause(item.cause));
  if (fileItems.length > 0) {
    areas.push({
      id: 'penalty',
      title: 'Penalty mitigation',
      badge: 'Fix these first',
      lead: PENALTY_LEAD,
      items: fileItems,
    });
  }
  if (intentItems.length > 0) {
    areas.push(intentArea(intentItems));
  }

  if (finiteBelow(v4.scores?.task_clarity, 8)) {
    areas.push({
      id: 'clarity',
      title: 'Make the ask clearer',
      badge: 'Task clarity',
      lead: 'Your goal was easy to follow, but the exact rules were left for the AI to guess.',
      nextTime: CLARITY_NEXT,
    });
  }

  const contextNotApplicable =
    v4.prompt_context_status === 'not_applicable' || v4.derived?.prompt_context_not_applicable === true;
  if (!contextNotApplicable && finiteBelow(v4.scores?.prompt_context, 8)) {
    areas.push({
      id: 'context',
      title: 'Point the AI at the work',
      badge: 'Prompt context',
      lead: 'The AI had to search for the code you meant. The more of the real code you hand over, the closer the first answer will be.',
      nextTime: CONTEXT_NEXT,
    });
  }

  const typeScore = v4.derived?.type_score;
  if (finiteBelow(typeScore, 1) || scoreMissing(typeScore)) {
    areas.push({
      id: 'type',
      title: 'Lean on approved skills',
      badge: 'Type score',
      lead: typeScoreLead(v4),
      nextTime: TYPE_NEXT,
    });
  }

  if (finiteBelow(v4.derived?.relevance_score, 1)) {
    areas.push({
      id: 'relevance',
      title: 'Attach only what fits',
      badge: 'Relevance',
      lead: 'Not every skill you attached matched the work in this prompt. A skill that does not help with the task adds nothing.',
      nextTime: RELEVANCE_NEXT,
    });
  }

  return areas;
}

export function judgeReasons(v4: ScoreDetailsV4): { tc: string; pc: string } {
  return justificationNotes(v4.justification);
}

export function skillRows(v4: ScoreDetailsV4): ScoreFileDetail[] {
  return (v4.derived?.file_details ?? []).filter((file) => file.kind === 'skill');
}

export function formatNum(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  return Number(value).toFixed(digits);
}

/** Type score, relevance score, and skill credit in the skill formula. Missing values read NA. */
export function formatSkillTerm(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return 'NA';
  return Number(value).toFixed(digits);
}

export function formatSkillCredit(value: number | null | undefined): string {
  const formatted = formatSkillTerm(value, 2);
  return formatted === 'NA' ? 'NA' : `+${formatted}`;
}
